import React, { useEffect, useState } from "react";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import PublishedWithChangesRounded from "@mui/icons-material/PublishedWithChangesRounded";
import BadgeOutlined from "@mui/icons-material/BadgeOutlined";
import HistoryRounded from "@mui/icons-material/HistoryRounded";
import Cached from "@mui/icons-material/Cached";
import { useRecoilValue } from "recoil";
import { userCredentials } from "recoilState/GlobalState";
import { Buttons, EstadoAgenteChip, HeaderButton } from "components";
import Modal from "components/UI/Modal/Modal";
import XGrid from "components/UI/Grid/XGrid";
import CeldaTruncada from "components/UI/Grid/CeldaTruncada";
import { agenteDetailsTitles } from "consts/titulos-de-tablas";
import { RNHAgenteGet } from "services/services-mia/programa-salud/personal/RNHAgenteGet";
import { transformDate } from "services/Local-services";
import { useService, puedeEditarServicio } from "components/route/ServiceRoute";
import {
  estaDeBaja,
  etiquetaEstado,
  etiquetaTipoHorario,
  MATRICULA_NO_APLICA,
} from "../consts";
import AsignarModal from "./AsignarModal";
import DesasignarModal from "./DesasignarModal";
import LaboralesModal from "./LaboralesModal";
import EliminarModal from "./EliminarModal";
import EstadoLugarModal from "./EstadoLugarModal";
import HistorialModal from "./HistorialModal";
import styles from "./DetalleModal.module.scss";
import { Add } from "@mui/icons-material";

/**
 * Las columnas que CORTAN con puntos suspensivos y muestran el valor entero en un
 * tooltip al pasar el mouse.
 *
 * Son las dos de nombre propio de la fila —el cargo y el hospital—, que es lo que deja
 * bajarles el ancho minimo sin perder el dato: al que no entra se lo lee en el tooltip.
 * Esta grilla lleva ocho columnas dentro de un modal, asi que cada minimo que se baja es
 * scroll horizontal que no aparece.
 *
 * El renderCell se inyecta aca y no en `titulos-de-tablas` para que ese archivo siga
 * siendo solo datos, sin importar componentes. Es una constante de modulo: recalcularlo
 * en cada render le cambiaria la identidad a las columnas al grid.
 */
const CAMPOS_TRUNCADOS = ["cargo", "hospital"];

const titles = agenteDetailsTitles.map((t) =>
  CAMPOS_TRUNCADOS.includes(t.field)
    ? { ...t, renderCell: ({ value }) => <CeldaTruncada value={value} /> }
    : t
);

/**
 * Un arreglo anidado del detalle, siempre como array.
 *
 * Según el driver puede llegar como texto. Si viniera roto se prefiere una tabla vacía
 * antes que tirar la vista entera.
 */
function comoArray(valor) {
  let v = valor;
  if (typeof v === "string") {
    try {
      v = JSON.parse(v);
    } catch {
      return [];
    }
  }
  return Array.isArray(v) ? v : [];
}

function datosDelLugar(l, agente) {
  return {
    version: l.rnhAgenteLugarVersion,
    estructuraId: l.rnhAgenteLugarEstructuraId,
    lugar: l.rnhAgenteLugarNombre ?? "",
    hospital: l.rnhAgenteLugarHospitalNombre ?? "",
    funcion: l.rnhAgenteLugarFuncion || "Sin función",
    funcionCod: l.rnhAgenteLugarFuncionCod,
    // El TIPO es del agente y el HORARIO de la asignación: juntos explican por qué un
    // lugar tiene horario y otro no.
    horarioTipo: etiquetaTipoHorario(agente?.rnhAgenteHorarioTipo),
    // PENDIENTE DE CONFIRMAR CONTRA UNA RESPONSE REAL. El param de AsignarDo es
    // `rnhAgenteLugarHorarioId` y todavía no vimos con qué nombre vuelve el nombre del
    // horario. Se prueban los dos candidatos antes de darlo por vacío, para no pintar la
    // celda en blanco si el back eligió el otro.
    horario: l.rnhAgenteLugarHorarioNombre || l.rnhHorarioNombre || "Sin horario",
    cargo:
      l.rnhAgenteLugarCargo === "Y"
        ? l.rnhAgenteLugarCargoNombre || agente?.rnhCargoNombre || "Sí"
        : "",
    esPrincipal: l.rnhLugarPrincipal === "Y",
    status: etiquetaEstado(l.rnhAgenteLugarStatusCod),
    statusCod: l.rnhAgenteLugarStatusCod,
  };
}

function filasDeLugares(agente) {
  return comoArray(agente?.rnhAgenteLugares).map((l, i) => ({
    ...datosDelLugar(l, agente),
    id: l.rnhAgenteLugarId ?? `vig-${i}`,
    fechaEstado: l.rnhAgenteLugarStatusFH || null,
  }));
}

/**
 * Detalle de un agente: su ficha arriba y una fila por ASIGNACION VIGENTE abajo.
 *
 * SOLO LO VIGENTE. Todo lo que paso vive en el boton Historial, completo y sin
 * filtrar. Mezclarlos obligaba a descartar las modificaciones para que la tabla no
 * repitiera un lugar por cada vez que lo tocaron, y un lugar desasignado y vuelto a
 * asignar aparecia dos veces sin forma de distinguirlas.
 *
 * `esPrincipal` SALE DE `rnhLugarPrincipal` Y NO DE `rnhAgenteLugarCargo`. Parecen el
 * mismo dato porque donde se ejerce el cargo es principal por definicion, pero solo
 * vale en esa direccion: un agente sin cargo tiene todos sus lugares en "N" y aun asi
 * uno es el principal. Justo ahi es cuando mas hace falta la marca.
 *
 * LA CARD SALE DE `datosAgente`, que se vuelve a pedir en cada refresco; la fila del
 * listado es solo el respaldo del primer render. Va con un ternario y no con `??`
 * campo por campo: una vez que el detalle esta, manda entero, porque con `??` un valor
 * que el detalle devuelve vacio caeria al de la fila y mostraria el viejo.
 *
 * LA SELECCION SE LIMPIA ANTES DE TRAER. Despues de cualquier operacion la version del
 * lugar cambio, y si la fila quedara elegida la proxima accion mandaria la vieja.
 *
 * De lectura para quien no puede escribir: los botones de abajo son la puerta de atras
 * a todo lo que la grilla ya le cierra a un Visualizador.
 */
export default function DetalleModal({ row, onCambio = () => {} }) {
  const credentials = useRecoilValue(userCredentials);
  const [asignaciones, setAsignaciones] = useState([]);
  const [datosAgente, setDatosAgente] = useState(null);
  const [rowSelected, setRowSelected] = useState({ status: false, row: {} });
  const [loading, setLoading] = useState(false);
  const [asignarOpen, setAsignarOpen] = useState(false);
  const [laboralesOpen, setLaboralesOpen] = useState(false);
  const [desasignarOpen, setDesasignarOpen] = useState(false);
  const [eliminarOpen, setEliminarOpen] = useState(false);
  const [estadoLugarOpen, setEstadoLugarOpen] = useState(false);
  const [historialOpen, setHistorialOpen] = useState(false);

  const deBaja = estaDeBaja(datosAgente?.usuarioVinculoStatus);

  const asignacionElegida = rowSelected.row?.id ? rowSelected.row : null;
  const { service } = useService();
  const puedeEscribir = puedeEditarServicio(service);

  const puedeOperarSobreAsignacion =
    puedeEscribir && Boolean(asignacionElegida) && !loading && !deBaja;

  const nombreCompleto = datosAgente
    ? [datosAgente.usuarioApellidos, datosAgente.usuarioNombres]
        .filter(Boolean)
        .join(", ")
    : row?.apellidosNombres;
  const legajo = datosAgente ? datosAgente.rnhAgenteLegajo : row?.legajo;

  const limpiarSeleccion = () => setRowSelected({ status: false, row: {} });

  const alCambiar = () => {
    getAsignaciones();
    onCambio();
  };

  const getAsignaciones = async () => {
    if (!row?.id) return;
    limpiarSeleccion();
    setLoading(true);
    const respuesta = await RNHAgenteGet(credentials, { agenteId: row.id });
    setLoading(false);

    if (!respuesta.ok) return;

    const agente = respuesta.data[0] ?? null;
    setDatosAgente(agente);
    setAsignaciones(filasDeLugares(agente));
  };

  useEffect(() => {
    getAsignaciones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.contenedor}>
      <div className={styles.cabecera}>
        <div className={styles.fichaConAccion}>
          <div className={styles.ficha}>
            <div className={styles.encabezado}>
              <span className={styles.nombre}>{nombreCompleto}</span>
              <span className={styles.estado}>
                <span className={styles.estadoLabel}>Fecha de inicio:</span>
                <span className={styles.dato}>
                  {datosAgente?.rnhAgenteFechaInicio
                    ? transformDate(datosAgente.rnhAgenteFechaInicio)
                    : "—"}
                </span>
              </span>
            </div>
            <span className={styles.dato}>
              Legajo: {legajo || "sin asignar"}
            </span>
            <div className={styles.estados}>
              <span className={styles.estado}>
                <span className={styles.estadoLabel}>Estado:</span>
                <EstadoAgenteChip cod={datosAgente?.usuarioVinculoStatus} />
              </span>
              <span className={styles.estado}>
                <span className={styles.estadoLabel}>Título:</span>
                <span className={styles.dato}>
                  {datosAgente?.rnhTituloNombre || "Sin asignar"}
                </span>
              </span>
              <span className={styles.estado}>
                <span className={styles.estadoLabel}>Matrícula:</span>
                <span className={styles.dato}>
                  {datosAgente?.rnhAgenteMatricula || MATRICULA_NO_APLICA}
                </span>
              </span>
            </div>
          </div>
          <HeaderButton
            text={"Datos laborales"}
            onClick={() => setLaboralesOpen(true)}
            disabled={!puedeEscribir || loading || !datosAgente || deBaja}
          >
            <BadgeOutlined />
          </HeaderButton>
          <HeaderButton
            text={"Historial"}
            onClick={() => setHistorialOpen(true)}
            disabled={loading || !datosAgente}
          >
            <HistoryRounded />
          </HeaderButton>
        </div>

        <Buttons>
          <HeaderButton
            text={"Actualizar"}
            onClick={getAsignaciones}
            disabled={loading}
          >
            <Cached />
          </HeaderButton>
          <HeaderButton
            text={"Asignar a repartición"}
            onClick={() => setAsignarOpen(true)}
            disabled={!puedeEscribir || loading || !datosAgente || deBaja}
          >
            <Add />
          </HeaderButton>
          <HeaderButton
            text={"Estado de la asignación"}
            onClick={() => setEstadoLugarOpen(true)}
            disabled={!puedeOperarSobreAsignacion}
          >
            <PublishedWithChangesRounded />
          </HeaderButton>
          <HeaderButton
            text={"Eliminar asignación"}
            onClick={() => setEliminarOpen(true)}
            disabled={!puedeOperarSobreAsignacion}
          >
            <DeleteOutlineRounded />
          </HeaderButton>
        </Buttons>
      </div>

      <div className={styles.tabla}>
        <XGrid
          rows={asignaciones}
          titles={titles}
          rowClassName={({ row }) =>
            row?.esPrincipal ? styles.filaPrincipal : ""
          }
          loading={loading}
          secondarySelected={rowSelected}
          setSecondarySelected={setRowSelected}
        />
      </div>

      <Modal
        size="lg"
        open={asignarOpen}
        title={`Asignar a ${nombreCompleto || "un componente"}`}
        onClose={() => setAsignarOpen(false)}
      >
        <AsignarModal
          row={row}
          datosAgente={datosAgente}
          updateList={alCambiar}
          closeModal={() => setAsignarOpen(false)}
        />
      </Modal>

      <Modal
        size="md"
        open={laboralesOpen}
        title={"Modificar Datos Laborales"}
        onClose={() => setLaboralesOpen(false)}
      >
        <LaboralesModal
          row={row}
          datosAgente={datosAgente}
          updateList={alCambiar}
          closeModal={() => setLaboralesOpen(false)}
        />
      </Modal>

      <Modal
        size="sm"
        open={desasignarOpen && Boolean(asignacionElegida)}
        title={"Desasignar Agente"}
        onClose={() => setDesasignarOpen(false)}
      >
        <DesasignarModal
          row={row}
          datosAgente={datosAgente}
          asignacion={asignacionElegida}
          updateList={alCambiar}
          limpiarSeleccion={limpiarSeleccion}
          closeModal={() => setDesasignarOpen(false)}
        />
      </Modal>

      <Modal
        size="sm"
        open={estadoLugarOpen && Boolean(asignacionElegida)}
        title={"Estado de la Asignación"}
        onClose={() => setEstadoLugarOpen(false)}
      >
        <EstadoLugarModal
          row={row}
          asignacion={asignacionElegida}
          updateList={alCambiar}
          closeModal={() => setEstadoLugarOpen(false)}
        />
      </Modal>

      <Modal
        size="xl"
        open={historialOpen}
        title={"Historial de Asignaciones"}
        onClose={() => setHistorialOpen(false)}
      >
        <HistorialModal
          datosAgente={datosAgente}
          closeModal={() => setHistorialOpen(false)}
        />
      </Modal>

      <Modal
        size="sm"
        open={eliminarOpen && Boolean(asignacionElegida)}
        title={"Eliminar Asignación"}
        onClose={() => setEliminarOpen(false)}
      >
        <EliminarModal
          row={row}
          asignacion={asignacionElegida}
          updateList={alCambiar}
          limpiarSeleccion={limpiarSeleccion}
          closeModal={() => setEliminarOpen(false)}
        />
      </Modal>
    </div>
  );
}
